import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import FileUpload from '../../../components/ui/FileUpload';
import Icon from '../../../components/AppIcon';
import { validateFileUpload } from './FormValidation';

const ComplianceStep = ({ formData, updateFormData, errors, onPreviewDocument, keepExistingDocuments, setKeepExistingDocuments, uploadedFiles }) => {
  const [currencySearch, setCurrencySearch] = useState('');

  const currencies = [
    { value: 'INR', label: 'INR - Indian Rupee' },
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'JPY', label: 'JPY - Japanese Yen' },
    { value: 'AUD', label: 'AUD - Australian Dollar' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
    { value: 'CHF', label: 'CHF - Swiss Franc' },
    { value: 'CNY', label: 'CNY - Chinese Yuan' },
    { value: 'SGD', label: 'SGD - Singapore Dollar' },
    { value: 'AED', label: 'AED - UAE Dirham' },
    { value: 'SAR', label: 'SAR - Saudi Riyal' }
  ];

  const natureOfAssessee = [
    { value: 'individual', label: 'Individual' },
    { value: 'huf', label: 'Hindu Undivided Family (HUF)' },
    { value: 'company', label: 'Company' },
    { value: 'firm', label: 'Firm/LLP' },
    { value: 'aop', label: 'Association of Persons (AOP)' },
    { value: 'body-of-individuals', label: 'Body of Individuals (BOI)' },
    { value: 'local-authority', label: 'Local Authority' },
    { value: 'artificial-juridical-person', label: 'Artificial Juridical Person' },
    { value: 'trust', label: 'Trust' },
    { value: 'society', label: 'Society' }
  ];

  const handleInputChange = (field, value) => {
    // Auto-uppercase PAN
    if (field === 'panNumber') {
      value = value.toUpperCase();
    }
    updateFormData({ [field]: value });
  };

  const handleFileUpload = (field, file) => {
    // FileUpload component now handles validation internally
    updateFormData({ [field]: file });
  };

  const handleRemoveFile = (field) => {
    updateFormData({ [field]: null });
  };

  const isIndian = formData.registeredCountry === 'IN';

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-2">Compliance Information</h2>
        <p className="text-text-secondary">
          Provide tax and regulatory compliance details as per your country's requirements.
        </p>
      </div>

      {/* Preferred Currency moved to Categorization step */}

      {/* India-specific fields */}
      {isIndian && (
        <div className="bg-muted/30 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">India-specific Compliance</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Input
                label="PAN Number"
                type="text"
                placeholder="Enter PAN number"
                value={formData.panNumber}
                onChange={(e) => handleInputChange('panNumber', e.target.value)}
                error={errors.panNumber}
                maxLength={10}
                required
              />
              {/* Show checkbox if document is pre-filled */}
              {uploadedFiles?.PAN_CARD && !formData.panDocument?.name && (
                <div className="mb-2 p-3 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="keepPanDocument"
                        checked={keepExistingDocuments.panDocument || false}
                        onChange={(e) => setKeepExistingDocuments(prev => ({ ...prev, panDocument: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="keepPanDocument" className="text-sm text-foreground cursor-pointer">
                        Keep existing: <span className="font-medium">{uploadedFiles.PAN_CARD.file_name}</span>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => onPreviewDocument && onPreviewDocument(uploadedFiles.PAN_CARD, 'PAN_CARD')}
                      className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors flex items-center space-x-1"
                    >
                      <Icon name="eye" className="w-4 h-4" />
                      <span>Preview</span>
                    </button>
                  </div>
                </div>
              )}
              {(!keepExistingDocuments.panDocument || !uploadedFiles?.PAN_CARD) && (
                <FileUpload
                  label="Upload PAN Document"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={2 * 1024 * 1024}
                  value={formData.panDocument}
                  onChange={(file) => handleFileUpload('panDocument', file)}
                  onRemove={() => handleRemoveFile('panDocument')}
                  onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'PAN_CARD')}
                  error={errors.panDocument}
                  required
                  description="Max 2MB - PDF, JPG, or PNG format"
                />
              )}
            </div>

            <div className="space-y-2">
              <Input
                label="GST Number"
                type="text"
                placeholder="Enter GST number"
                value={formData.gstNumber}
                onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                error={errors.gstNumber}
                maxLength={15}
                required
              />
              {/* Show checkbox if document is pre-filled */}
              {uploadedFiles?.GST_CERTIFICATE && !formData.gstDocument?.name && (
                <div className="mb-2 p-3 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="keepGstDocument"
                        checked={keepExistingDocuments.gstDocument || false}
                        onChange={(e) => setKeepExistingDocuments(prev => ({ ...prev, gstDocument: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="keepGstDocument" className="text-sm text-foreground cursor-pointer">
                        Keep existing: <span className="font-medium">{uploadedFiles.GST_CERTIFICATE.file_name}</span>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => onPreviewDocument && onPreviewDocument(uploadedFiles.GST_CERTIFICATE, 'GST_CERTIFICATE')}
                      className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors flex items-center space-x-1"
                    >
                      <Icon name="eye" className="w-4 h-4" />
                      <span>Preview</span>
                    </button>
                  </div>
                </div>
              )}
              {(!keepExistingDocuments.gstDocument || !uploadedFiles?.GST_CERTIFICATE) && (
                <FileUpload
                  label="Upload GST Document"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={3 * 1024 * 1024}
                  value={formData.gstDocument}
                  onChange={(file) => handleFileUpload('gstDocument', file)}
                  onRemove={() => handleRemoveFile('gstDocument')}
                  onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'GST_CERTIFICATE')}
                  error={errors.gstDocument}
                  required
                  description="Max 3MB - PDF, JPG, or PNG format"
                />
              )}
            </div>

            <Select
              label="Nature of Assessee"
              description="Select your tax assessee category"
              options={natureOfAssessee}
              value={formData.natureOfAssessee}
              onChange={(value) => handleInputChange('natureOfAssessee', value)}
              error={errors.natureOfAssessee}
              required
            />

            <Select
              label="GTA (Goods Transport Agency) Registration"
              description="Are you registered as a Goods Transport Agency under GST?"
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ]}
              value={formData.gtaRegistration}
              onChange={(value) => handleInputChange('gtaRegistration', value)}
              error={errors.gtaRegistration}
              required
            />

            {/* Place of Supply removed as per requirement */}
          </div>
        </div>
      )}

      {/* International compliance */}
      {!isIndian && (
        <div className="bg-muted/30 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">International Compliance</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="VAT/Sales Tax Number"
              type="text"
              placeholder="Enter VAT or sales tax number"
              value={formData.vatNumber}
              onChange={(e) => handleInputChange('vatNumber', e.target.value)}
              error={errors.vatNumber}
            />

            <Input
              label="Business License Number"
              type="text"
              placeholder="Enter business license number"
              value={formData.businessLicense}
              onChange={(e) => handleInputChange('businessLicense', e.target.value)}
              error={errors.businessLicense}
              required
            />

            <Select
              label="GTA (Goods Transport Agency) Registration"
              description="Are you registered as a Goods Transport Agency under GST?"
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ]}
              value={formData.gtaRegistration}
              onChange={(value) => handleInputChange('gtaRegistration', value)}
              error={errors.gtaRegistration}
              required
            />

            <div className="md:col-span-2">
              <Input
                label="Regulatory Compliance Notes"
                type="text"
                placeholder="Any additional regulatory compliance information"
                value={formData.complianceNotes}
                onChange={(e) => handleInputChange('complianceNotes', e.target.value)}
                error={errors.complianceNotes}
              />
            </div>
          </div>
        </div>
      )}

      {/* Additional Information section removed as per requirement */}

      {/* Information Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center mt-0.5">
            <span className="text-white text-xs font-bold">!</span>
          </div>
          <div>
            <h4 className="font-medium text-amber-900 mb-1">Compliance Verification</h4>
            <p className="text-sm text-amber-800">
              All compliance information will be verified during the approval process. 
              Please ensure all details are accurate and up-to-date. Supporting documents 
              may be requested for verification.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceStep;